import classNames from "classnames";
import {
	DateString,
	Icon,
	PersonAvatar
} from "client/Components";
import { getContactPhotoUrl } from "client/Features/AddressBook/common";
import { EmailImportance } from "client/Api/Email/common";
import { EmailActions, EmailListItemModel } from "client/Api/Email/email";
import { AlertManager, useAlerts } from "client/Hooks";
import { generateTrackingId, isNotNil } from "client/Misc/utils";
import { OwaEmailClient, WorkspaceEmailClient } from "client_legacy/Email/emailClient";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { ListItem } from "../ListItem";
import styles from "./EmailListItem.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type EmailListItemProps = {
	message: EmailListItemModel;
	smtpAddress: string | null;
	shouldUseWideTemplate: boolean;
	emailClient: WorkspaceEmailClient | OwaEmailClient;
	timezone: TimeZone;
	onClick(): void;
};

export const EmailListItem = (props: EmailListItemProps): ReactElement => {
	const {
		message,
		smtpAddress,
		shouldUseWideTemplate,
		emailClient,
		timezone,
		onClick
	} = props;

	const alertManager: AlertManager = useAlerts();

	const navigateHandler = useCallback(
		(): void => {
			onClick();

			if (emailClient.supportsNavigateToEmail) {
				emailClient.navigateToEmail({
					emailId: message.emailId
						.id,
					mailbox: smtpAddress
				});
			} else {
				console.error("The email client does not support navigating to an email", emailClient);
				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			alertManager,
			emailClient,
			message.emailId.id,
			onClick,
			smtpAddress
		]
	);

	// Because the sender name can be any arbitrary value (and is not split into e.g. Firstname/Lastname), we only take
	// the first character as the "initial"
	const senderInitials: string = message.ownerName?.[0] ?? "";

	return (
		<ListItem
			trackingId={generateTrackingId("email", `|${message.emailId.id}`)}
			isHoverable
			isWide={shouldUseWideTemplate}
			onClick={navigateHandler}
			title={message.subject}
		>
			{shouldUseWideTemplate && (
				<ListItem.Icon>
					<PersonAvatar
						initials={senderInitials}
						imageSrc={
							isNotNil(message.from)
								? getContactPhotoUrl(message.from)
								: undefined
						}
					/>
				</ListItem.Icon>
			)}

			<ListItem.Content>
				<ListItem.Row verticalAlignment="center">
					<ListItem.Title trackingId="title">
						<span
							className={classNames(
								{ [styles.subject_unread]: !message.isRead}
							)}
						>
							{message.subject}
						</span>
					</ListItem.Title>

					<ListItem.RightAddition verticalAlignment="center">
						<IconList message={message} />

						{shouldUseWideTemplate && (
							<ListItem.Info>
								{message.receivedDateTime && (
									<span className={styles.receivedDate}>
										<DateString
											value={message.receivedDateTime}
											format="AbsoluteTimestamp"
											timezone={timezone}
										/>
									</span>
								)}
							</ListItem.Info>
						)}
					</ListItem.RightAddition>
				</ListItem.Row>

				<ListItem.Row>
					<ListItem.Info>
						{message.ownerName}
					</ListItem.Info>
					{!shouldUseWideTemplate && (
						<ListItem.RightAddition>
							<ListItem.Info>
								{message.receivedDateTime && (
									<span className={styles.receivedDate}>
										<DateString
											value={message.receivedDateTime}
											format="AbsoluteTimestamp"
											timezone={timezone}
										/>
									</span>
								)}
							</ListItem.Info>
						</ListItem.RightAddition>
					)}
				</ListItem.Row>

				{shouldUseWideTemplate && (
					<ListItem.Description>
						{message.preview}
					</ListItem.Description>
				)}
			</ListItem.Content>
		</ListItem>
	);
};
EmailListItem.displayName = "EmailListItem";

type IconListProps = {
	message: EmailListItemModel;
};

const IconList = (props: IconListProps): ReactElement => {
	const {
		message
	} = props;

	const hasAttachments: boolean = message.hasAttachments;
	const isFlagged: boolean = message.isFlagged;
	const isReplied: boolean = message.lastAction === EmailActions.Replied;
	const isForwarded: boolean = message.lastAction === EmailActions.Forwarded;
	const isHighImportance: boolean = message.importance === EmailImportance.High;
	const isLowImportance: boolean = message.importance === EmailImportance.Low;
	const isMeeting: boolean = message.isMeetingMessage;

	return (
		<ul className={styles.iconList}>
			{isReplied && (
				<li>
					<Icon value="reply" />
				</li>
			)}

			{isForwarded && (
				<li>
					<Icon value="share" />
				</li>
			)}

			{isMeeting && (
				<li>
					<Icon value="calendar" />
				</li>
			)}

			{hasAttachments && (
				<li>
					<Icon value="paperclip" />
				</li>
			)}

			{isFlagged && (
				<li>
					<Icon value={{
						kind: "font",
						name: "flag",
						color: "danger"
					}} />
				</li>
			)}

			{isHighImportance && (
				<li>
					<Icon value="triangle-exclamation" />
				</li>
			)}

			{isLowImportance && (
				<li>
					<Icon value="arrow-down" />
				</li>
			)}
		</ul >
	);
};
IconList.displayName = "IconList";
